/**
 * Microsoft Graph API client for OneDrive/SharePoint file operations
 */

import { requestWithRetry } from "../utils/http.js";
import type { AuthProvider } from "../auth/types.js";
import { RESOURCES } from "../auth/types.js";
import { ApiError, RateLimitError } from "../utils/errors.js";
import { createChildLogger } from "../utils/logger.js";
import { validateSharePointHostname, sanitizeFilePath, validateGraphId, validateODataFilter, validateODataFieldList } from "../utils/security.js";

const graphLogger = createChildLogger({ module: "graph-api" });

const GRAPH_API_BASE = "https://graph.microsoft.com/v1.0";

export interface DriveItem {
  id: string;
  name: string;
  webUrl?: string;
  parentReference?: {
    driveId: string;
    path?: string;
  };
  file?: {
    mimeType: string;
  };
  folder?: {
    childCount: number;
  };
}

export interface ExcelTable {
  id: string;
  name: string;
  showHeaders: boolean;
  showTotals: boolean;
}

export interface ExcelColumn {
  id: string;
  name: string;
  index: number;
}

export interface ExcelWorksheet {
  id: string;
  name: string;
  position: number;
}

// SharePoint types
export interface SharePointSite {
  id: string;
  displayName: string;
  name: string;
  webUrl: string;
  description?: string;
  siteCollection?: { hostname: string };
}

export interface SharePointList {
  id: string;
  displayName: string;
  name: string;
  description?: string;
  webUrl: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  list: { template: string; hidden: boolean };
}

export interface SharePointColumn {
  id: string;
  displayName: string;
  name: string;
  description?: string;
  /**
   * Derived, not returned by Graph. Graph reports a column's type by which
   * FACET object is present on it (`text`, `choice`, `dateTime`, ...), not
   * via a `type` field — so this is filled in from that facet.
   */
  type: string;
  indexed: boolean;
  required: boolean;
  readOnly: boolean;
}

/**
 * The facets Graph uses to express a SharePoint column's type. Exactly one is
 * normally present on a column, and its key IS the type.
 */
const SHAREPOINT_COLUMN_TYPE_FACETS = [
  "text",
  "choice",
  "dateTime",
  "number",
  "currency",
  "boolean",
  "lookup",
  "personOrGroup",
  "hyperlinkOrPicture",
  "calculated",
  "geolocation",
  "term",
  "termSet",
  "thumbnail",
  "contentApprovalStatus",
  "approvalStatus",
] as const;

/** Read a column's type from whichever facet Graph attached to it. */
export function deriveSharePointColumnType(raw: Record<string, unknown>): string {
  for (const facet of SHAREPOINT_COLUMN_TYPE_FACETS) {
    if (raw[facet] !== undefined && raw[facet] !== null) return facet;
  }
  // Better to say the type was not reported than to assert a wrong one.
  return "unknown";
}

export interface SharePointListItem {
  id: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  webUrl: string;
  fields?: Record<string, unknown>;
  createdBy?: { user?: { displayName: string; email?: string } };
  lastModifiedBy?: { user?: { displayName: string; email?: string } };
}

export interface SharePointDrive {
  id: string;
  name: string;
  driveType: string;
  webUrl: string;
  description?: string;
  owner?: { user?: { displayName: string } };
}

export interface GraphApiConfig {
  authProvider: AuthProvider;
}

/**
 * Microsoft Graph API client
 */
export class GraphApi {
  private readonly authProvider: AuthProvider;

  constructor(config: GraphApiConfig) {
    this.authProvider = config.authProvider;
  }

  private async getAuthHeaders(contentType = "application/json"): Promise<Record<string, string>> {
    const token = await this.authProvider.getTokenForResource(RESOURCES.GRAPH);
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": contentType,
    };
  }

  /**
   * Handle binary response (file downloads)
   */
  private async handleBinaryResponse(
    response: Awaited<ReturnType<typeof requestWithRetry>>,
    context: string
  ): Promise<{ data: Buffer; contentType: string }> {
    const { statusCode, body, headers } = response;

    if (statusCode === 429) {
      await body.text();
      const retryAfter = response.headers["retry-after"];
      const retryMs = retryAfter ? parseInt(String(retryAfter), 10) * 1000 : undefined;
      throw new RateLimitError(retryMs);
    }

    if (statusCode >= 400) {
      const responseText = await body.text();
      graphLogger.error({ statusCode, context }, "Graph API binary response error");
      let errorDetails: unknown;
      try {
        const parsed = JSON.parse(responseText) as {
          error?: { code?: unknown; message?: unknown };
        };
        // Only extract error code/message, not full response
        errorDetails = parsed?.error
          ? { code: parsed.error.code, message: parsed.error.message }
          : "Graph API error";
      } catch {
        errorDetails = "Graph API error (non-JSON response)";
      }
      throw new ApiError(`${context}: Graph API error`, statusCode, errorDetails);
    }

    const MAX_DOWNLOAD_SIZE = 100 * 1024 * 1024; // 100MB
    const chunks: Buffer[] = [];
    let totalSize = 0;
    for await (const chunk of body as AsyncIterable<Uint8Array>) {
      totalSize += chunk.length;
      if (totalSize > MAX_DOWNLOAD_SIZE) {
        throw new ApiError(`${context}: file too large (max 100MB)`, 413);
      }
      chunks.push(Buffer.from(chunk));
    }
    const data = Buffer.concat(chunks);
    const contentType = (headers["content-type"] as string) || "application/octet-stream";

    return { data, contentType };
  }

  private async handleResponse<T>(
    response: Awaited<ReturnType<typeof requestWithRetry>>,
    context: string
  ): Promise<T> {
    const { statusCode, body } = response;

    if (statusCode === 429) {
      await body.text();
      const retryAfter = response.headers["retry-after"];
      const retryMs = retryAfter ? parseInt(String(retryAfter), 10) * 1000 : undefined;
      throw new RateLimitError(retryMs);
    }

    // Stream-read body with size limit to prevent OOM on large responses
    const MAX_RESPONSE_SIZE = 50 * 1024 * 1024; // 50MB
    const chunks: Buffer[] = [];
    let totalSize = 0;
    for await (const chunk of body as AsyncIterable<Uint8Array>) {
      totalSize += chunk.length;
      if (totalSize > MAX_RESPONSE_SIZE) {
        throw new ApiError(`${context}: response too large (max 50MB)`, 413);
      }
      chunks.push(Buffer.from(chunk));
    }
    const responseText = Buffer.concat(chunks).toString("utf-8");

    if (statusCode >= 400) {
      graphLogger.error({ statusCode, context }, "Graph API error");
      let errorDetails: unknown;
      try {
        errorDetails = JSON.parse(responseText);
      } catch {
        errorDetails = responseText;
      }
      throw new ApiError(`${context}: Graph API error`, statusCode, errorDetails);
    }

    if (!responseText) {
      return { value: [] } as unknown as T;
    }

    return JSON.parse(responseText) as T;
  }

  /**
   * Search for files in OneDrive by name
   */
  async searchFiles(query: string, driveId?: string): Promise<DriveItem[]> {
    graphLogger.debug({ query, driveId }, "Searching files");

    const headers = await this.getAuthHeaders();

    // Search in user's OneDrive
    const searchPath = driveId
      ? `/drives/${driveId}/root/search(q='${encodeURIComponent(query)}')`
      : `/me/drive/root/search(q='${encodeURIComponent(query)}')`;

    const url = `${GRAPH_API_BASE}${searchPath}?$select=id,name,webUrl,parentReference,file,folder&$top=25`;

    const response = await requestWithRetry(url, { method: "GET", headers });
    const data = await this.handleResponse<{ value: DriveItem[] }>(response, "Search files");

    graphLogger.info({ query, count: data.value.length }, "File search complete");
    return data.value;
  }

  /**
   * Get file by path in OneDrive
   */
  async getFileByPath(path: string): Promise<DriveItem> {
    graphLogger.debug({ path }, "Getting file by path");

    const headers = await this.getAuthHeaders();

    // Normalize path - ensure it starts with /
    let normalizedPath = path.startsWith("/") ? path : `/${path}`;

    // Encode each path segment individually (not the slashes)
    normalizedPath = normalizedPath
      .split("/")
      .map((segment) => encodeURIComponent(segment))
      .join("/");

    const url = `${GRAPH_API_BASE}/me/drive/root:${normalizedPath}?$select=id,name,webUrl,parentReference,file`;

    graphLogger.debug({ url }, "Fetching file by path");

    const response = await requestWithRetry(url, { method: "GET", headers });
    return this.handleResponse<DriveItem>(response, "Get file by path");
  }

  /**
   * List files in OneDrive root or a folder
   */
  async listFolder(folderPath?: string): Promise<DriveItem[]> {
    graphLogger.debug({ folderPath }, "Listing folder");

    const headers = await this.getAuthHeaders();

    let url: string;
    if (folderPath) {
      // Encode path segments
      const encodedPath = folderPath
        .split("/")
        .filter((s) => s)
        .map((segment) => encodeURIComponent(segment))
        .join("/");
      url = `${GRAPH_API_BASE}/me/drive/root:/${encodedPath}:/children?$select=id,name,webUrl,parentReference,file,folder&$top=50`;
    } else {
      url = `${GRAPH_API_BASE}/me/drive/root/children?$select=id,name,webUrl,parentReference,file,folder&$top=50`;
    }

    const response = await requestWithRetry(url, { method: "GET", headers });
    const data = await this.handleResponse<{ value: DriveItem[] }>(response, "List folder");
    return data.value;
  }

  /**
   * Get the user's default drive ID
   */
  async getMyDriveId(): Promise<string> {
    graphLogger.debug("Getting user drive ID");

    const headers = await this.getAuthHeaders();
    const url = `${GRAPH_API_BASE}/me/drive?$select=id`;

    const response = await requestWithRetry(url, { method: "GET", headers });
    const data = await this.handleResponse<{ id: string }>(response, "Get drive");

    return data.id;
  }

  /**
   * Get tables from an Excel file
   */
  async getExcelTables(driveId: string, itemId: string): Promise<ExcelTable[]> {
    graphLogger.debug({ driveId, itemId }, "Getting Excel tables");

    const headers = await this.getAuthHeaders();
    const url = `${GRAPH_API_BASE}/drives/${driveId}/items/${itemId}/workbook/tables?$select=id,name,showHeaders,showTotals`;

    const response = await requestWithRetry(url, { method: "GET", headers });
    const data = await this.handleResponse<{ value: ExcelTable[] }>(response, "Get Excel tables");

    graphLogger.info({ itemId, count: data.value.length }, "Got Excel tables");
    return data.value;
  }

  /**
   * Get columns from an Excel table
   */
  async getExcelTableColumns(
    driveId: string,
    itemId: string,
    tableId: string
  ): Promise<ExcelColumn[]> {
    graphLogger.debug({ driveId, itemId, tableId }, "Getting Excel table columns");

    const headers = await this.getAuthHeaders();
    const url = `${GRAPH_API_BASE}/drives/${driveId}/items/${itemId}/workbook/tables/${tableId}/columns?$select=id,name,index`;

    const response = await requestWithRetry(url, { method: "GET", headers });
    const data = await this.handleResponse<{ value: ExcelColumn[] }>(response, "Get Excel columns");

    graphLogger.info({ tableId, count: data.value.length }, "Got Excel columns");
    return data.value;
  }

  /**
   * Get worksheets from an Excel file
   */
  async getExcelWorksheets(driveId: string, itemId: string): Promise<ExcelWorksheet[]> {
    graphLogger.debug({ driveId, itemId }, "Getting Excel worksheets");

    const headers = await this.getAuthHeaders();
    const url = `${GRAPH_API_BASE}/drives/${driveId}/items/${itemId}/workbook/worksheets?$select=id,name,position`;

    const response = await requestWithRetry(url, { method: "GET", headers });
    const data = await this.handleResponse<{ value: ExcelWorksheet[] }>(response, "Get Excel worksheets");

    return data.value;
  }

  /**
   * Get a range of data from an Excel worksheet (for files without tables)
   */
  async getExcelUsedRange(
    driveId: string,
    itemId: string,
    worksheetName: string
  ): Promise<{ address: string; values: unknown[][]; columnCount: number; rowCount: number }> {
    graphLogger.debug({ driveId, itemId, worksheetName }, "Getting Excel used range");

    const headers = await this.getAuthHeaders();
    const url = `${GRAPH_API_BASE}/drives/${driveId}/items/${itemId}/workbook/worksheets/${encodeURIComponent(worksheetName)}/usedRange?$select=address,values,columnCount,rowCount`;

    const response = await requestWithRetry(url, { method: "GET", headers });
    return this.handleResponse(response, "Get Excel used range");
  }

  // ==========================================================================
  // SharePoint Sites
  // ==========================================================================

  /**
   * Search for SharePoint sites
   */
  async searchSites(query: string): Promise<SharePointSite[]> {
    graphLogger.debug({ query }, "Searching SharePoint sites");

    const headers = await this.getAuthHeaders();
    const url = `${GRAPH_API_BASE}/sites?search=${encodeURIComponent(query)}&$select=id,displayName,name,webUrl,description,siteCollection&$top=25`;

    const response = await requestWithRetry(url, { method: "GET", headers });
    const data = await this.handleResponse<{ value: SharePointSite[] }>(response, "Search sites");

    graphLogger.info({ query, count: data.value.length }, "Site search complete");
    return data.value;
  }

  /**
   * Get a SharePoint site by ID
   */
  async getSite(siteId: string): Promise<SharePointSite> {
    validateGraphId(siteId, "site");
    graphLogger.debug({ siteId }, "Getting SharePoint site");

    const headers = await this.getAuthHeaders();
    const url = `${GRAPH_API_BASE}/sites/${siteId}?$select=id,displayName,name,webUrl,description,siteCollection`;

    const response = await requestWithRetry(url, { method: "GET", headers });
    return this.handleResponse<SharePointSite>(response, `Get site ${siteId}`);
  }

  /**
   * Get a SharePoint site by URL (hostname and path)
   */
  async getSiteByUrl(hostname: string, sitePath: string): Promise<SharePointSite> {
    const safeHostname = validateSharePointHostname(hostname);
    graphLogger.debug({ hostname: safeHostname, sitePath }, "Getting SharePoint site by URL");

    const headers = await this.getAuthHeaders();
    const path = sitePath.startsWith("/") ? sitePath : `/${sitePath}`;
    const url = `${GRAPH_API_BASE}/sites/${safeHostname}:${path}?$select=id,displayName,name,webUrl,description,siteCollection`;

    const response = await requestWithRetry(url, { method: "GET", headers });
    return this.handleResponse<SharePointSite>(response, `Get site ${hostname}${path}`);
  }

  // ==========================================================================
  // SharePoint Lists
  // ==========================================================================

  /**
   * List all lists in a SharePoint site
   */
  async listSiteLists(siteId: string): Promise<SharePointList[]> {
    validateGraphId(siteId, "site");
    graphLogger.debug({ siteId }, "Listing SharePoint lists");

    const headers = await this.getAuthHeaders();
    const url = `${GRAPH_API_BASE}/sites/${siteId}/lists?$select=id,displayName,name,description,webUrl,createdDateTime,lastModifiedDateTime,list&$top=100`;

    const response = await requestWithRetry(url, { method: "GET", headers });
    const data = await this.handleResponse<{ value: SharePointList[] }>(response, "List site lists");

    graphLogger.info({ siteId, count: data.value.length }, "Listed site lists");
    return data.value;
  }

  /**
   * Get columns for a SharePoint list
   */
  async getListColumns(siteId: string, listId: string): Promise<SharePointColumn[]> {
    validateGraphId(siteId, "site");
    validateGraphId(listId, "list");
    graphLogger.debug({ siteId, listId }, "Getting list columns");

    const headers = await this.getAuthHeaders();
    // No $select: the previous one asked for id/displayName/name/description/
    // indexed/required/readOnly and therefore excluded EVERY type facet, so
    // `type` was always undefined and every column rendered as "unknown".
    // Graph does not return a `type` field — the type is which facet object is
    // present — so the whole column object is needed.
    const url = `${GRAPH_API_BASE}/sites/${siteId}/lists/${listId}/columns?$top=250`;

    const response = await requestWithRetry(url, { method: "GET", headers });
    const data = await this.handleResponse<{ value: Record<string, unknown>[] }>(
      response,
      "Get list columns",
    );

    const columns = data.value.map(
      (raw) => ({ ...raw, type: deriveSharePointColumnType(raw) }) as unknown as SharePointColumn,
    );
    graphLogger.info({ listId, count: columns.length }, "Got list columns");
    return columns;
  }

  // ==========================================================================
  // SharePoint Drives
  // ==========================================================================

  /**
   * List document libraries (drives) for a site
   */
  async listSiteDrives(siteId: string): Promise<SharePointDrive[]> {
    validateGraphId(siteId, "site");
    graphLogger.debug({ siteId }, "Listing site drives");

    const headers = await this.getAuthHeaders();
    const url = `${GRAPH_API_BASE}/sites/${siteId}/drives?$select=id,name,driveType,webUrl,description,owner`;

    const response = await requestWithRetry(url, { method: "GET", headers });
    const data = await this.handleResponse<{ value: SharePointDrive[] }>(response, "List site drives");

    graphLogger.info({ siteId, count: data.value.length }, "Listed site drives");
    return data.value;
  }

  // ==========================================================================
  // SharePoint List Items
  // ==========================================================================

  /**
   * Get items from a SharePoint list
   */
  async getListItems(
    siteId: string,
    listId: string,
    options: { filter?: string; select?: string; top?: number; expand?: string; orderBy?: string } = {}
  ): Promise<SharePointListItem[]> {
    validateGraphId(siteId, "site");
    validateGraphId(listId, "list");
    graphLogger.debug({ siteId, listId, ...options }, "Getting list items");

    const headers = await this.getAuthHeaders();
    const parts = ["$expand=fields"];
    if (options.filter) parts.push(`$filter=${validateODataFilter(options.filter)}`);
    if (options.select) parts.push(`$select=${validateODataFieldList(options.select, "$select")}`);
    if (options.orderBy) parts.push(`$orderby=${validateODataFieldList(options.orderBy, "$orderby")}`);
    parts.push(`$top=${options.top ?? 50}`);

    const url = `${GRAPH_API_BASE}/sites/${siteId}/lists/${listId}/items?${parts.join("&")}`;

    const response = await requestWithRetry(url, { method: "GET", headers });
    const data = await this.handleResponse<{ value: SharePointListItem[] }>(response, "Get list items");

    graphLogger.info({ listId, count: data.value.length }, "Got list items");
    return data.value;
  }

  /**
   * Create a new list item
   */
  async createListItem(
    siteId: string,
    listId: string,
    fields: Record<string, unknown>
  ): Promise<SharePointListItem> {
    validateGraphId(siteId, "site");
    validateGraphId(listId, "list");
    graphLogger.info({ siteId, listId }, "Creating list item");

    const headers = await this.getAuthHeaders();
    const url = `${GRAPH_API_BASE}/sites/${siteId}/lists/${listId}/items`;

    const response = await requestWithRetry(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ fields }),
    });

    const item = await this.handleResponse<SharePointListItem>(response, "Create list item");
    graphLogger.info({ listId, itemId: item.id }, "Created list item");
    return item;
  }

  /**
   * Update a list item's fields
   */
  async updateListItem(
    siteId: string,
    listId: string,
    itemId: string,
    fields: Record<string, unknown>
  ): Promise<Record<string, unknown>> {
    validateGraphId(siteId, "site");
    validateGraphId(listId, "list");
    validateGraphId(itemId, "item");
    graphLogger.info({ siteId, listId, itemId }, "Updating list item");

    const headers = await this.getAuthHeaders();
    const url = `${GRAPH_API_BASE}/sites/${siteId}/lists/${listId}/items/${itemId}/fields`;

    const response = await requestWithRetry(url, {
      method: "PATCH",
      headers,
      body: JSON.stringify(fields),
    });

    const updated = await this.handleResponse<Record<string, unknown>>(response, `Update item ${itemId}`);
    graphLogger.info({ listId, itemId }, "Updated list item");
    return updated;
  }

  /**
   * Delete a list item
   */
  async deleteListItem(siteId: string, listId: string, itemId: string): Promise<void> {
    validateGraphId(siteId, "site");
    validateGraphId(listId, "list");
    validateGraphId(itemId, "item");
    graphLogger.info({ siteId, listId, itemId }, "Deleting list item");

    const headers = await this.getAuthHeaders();
    const url = `${GRAPH_API_BASE}/sites/${siteId}/lists/${listId}/items/${itemId}`;

    const response = await requestWithRetry(url, { method: "DELETE", headers });
    if (response.statusCode >= 400) {
      await this.handleResponse<unknown>(response, `Delete item ${itemId}`);
    } else {
      await response.body.text();
    }
    graphLogger.info({ listId, itemId }, "Deleted list item");
  }

  // ==========================================================================
  // SharePoint Files (via Drives)
  // ==========================================================================

  /**
   * List files in a site's drive root or folder
   */
  async listSiteFiles(
    siteId: string,
    driveId: string,
    folderPath?: string
  ): Promise<DriveItem[]> {
    validateGraphId(siteId, "site");
    validateGraphId(driveId, "drive");
    graphLogger.debug({ siteId, driveId, folderPath }, "Listing site files");

    const headers = await this.getAuthHeaders();

    let url: string;
    if (folderPath) {
      const safeFolderPath = sanitizeFilePath(folderPath);
      const encodedPath = safeFolderPath
        .split("/")
        .filter((s) => s)
        .map((segment) => encodeURIComponent(segment))
        .join("/");
      url = `${GRAPH_API_BASE}/sites/${siteId}/drives/${driveId}/root:/${encodedPath}:/children?$select=id,name,webUrl,parentReference,file,folder&$top=100`;
    } else {
      url = `${GRAPH_API_BASE}/sites/${siteId}/drives/${driveId}/root/children?$select=id,name,webUrl,parentReference,file,folder&$top=100`;
    }

    const response = await requestWithRetry(url, { method: "GET", headers });
    const data = await this.handleResponse<{ value: DriveItem[] }>(response, "List site files");
    return data.value;
  }

  /**
   * Upload a file to a SharePoint document library (simple upload, ≤ 4MB)
   */
  async uploadSiteFile(
    siteId: string,
    driveId: string,
    filePath: string,
    content: Buffer | string,
    contentType = "application/octet-stream"
  ): Promise<DriveItem> {
    validateGraphId(siteId, "site");
    validateGraphId(driveId, "drive");
    const safePath = sanitizeFilePath(filePath);
    graphLogger.info({ siteId, driveId, filePath: safePath }, "Uploading file to SharePoint");

    const headers = await this.getAuthHeaders(contentType);
    const encodedPath = safePath
      .split("/")
      .filter((s) => s)
      .map((segment) => encodeURIComponent(segment))
      .join("/");

    const url = `${GRAPH_API_BASE}/sites/${siteId}/drives/${driveId}/root:/${encodedPath}:/content`;

    const body = typeof content === "string" ? Buffer.from(content) : content;

    const response = await requestWithRetry(url, {
      method: "PUT",
      headers,
      body,
    });

    const item = await this.handleResponse<DriveItem>(response, `Upload file ${filePath}`);
    graphLogger.info({ filePath, itemId: item.id }, "Uploaded file");
    return item;
  }

  /**
   * Get file content from a SharePoint document library
   */
  async getSiteFileContent(
    siteId: string,
    driveId: string,
    itemId: string
  ): Promise<{ data: Buffer; contentType: string }> {
    validateGraphId(siteId, "site");
    validateGraphId(driveId, "drive");
    validateGraphId(itemId, "item");
    graphLogger.debug({ siteId, driveId, itemId }, "Getting file content");

    const headers = await this.getAuthHeaders();
    delete headers["Content-Type"]; // Not needed for GET
    const url = `${GRAPH_API_BASE}/sites/${siteId}/drives/${driveId}/items/${itemId}/content`;

    const response = await requestWithRetry(url, { method: "GET", headers });
    return this.handleBinaryResponse(response, `Get file content ${itemId}`);
  }
}
